export const SEARCH_DRAWER_EVENT = Object.freeze({
  event: 'lp_click_top_search',
  lp_track: 'click_top_search',
  lp_track_context: 'header',
  lp_track_source: 'vendor',
})

export const ADD_TO_CART_SUCCESS_EVENT = Object.freeze({
  event: 'lp_custom_add_to_cart_success',
  lp_track: 'custom_add_to_cart',
  lp_track_source: 'vendor',
  lp_tracking_owner: 'impact_variant_add',
})

export const FAQ_CLICK_EVENT = Object.freeze({
  event: 'lp_click_faq',
  lp_track: 'click_faq',
  lp_track_context: 'pdp_faq',
  lp_track_source: 'vendor',
})

export const KLAVIYO_SUBSCRIBE_SUCCESS_EVENT = Object.freeze({
  event: 'lp_subscribe_success',
  lp_track: 'subscribe',
  lp_track_source: 'vendor',
  lp_tracking_owner: 'klaviyo_forms_submit',
})

export const NEWSLETTER_SUBSCRIBE_INTENT_EVENT = Object.freeze({
  event: 'lp_subscribe_intent',
  lp_track: 'subscribe',
  lp_track_context: 'footer',
  lp_track_source: 'vendor',
  lp_tracking_owner: 'shopify_newsletter_submit',
})

export const HEADER_CATEGORY_SELECT_EVENTS = Object.freeze({
  select_top_category: Object.freeze({
    event: 'lp_select_top_category',
    lp_track: 'select_top_category',
  }),
  select_second_category: Object.freeze({
    event: 'lp_select_second_category',
    lp_track: 'select_second_category',
  }),
})

// Backward-compatible aliases for consumers that imported the phase-seven names.
export const COLLECTION_QUICK_ADD_SUCCESS_EVENT = ADD_TO_CART_SUCCESS_EVENT
export const PDP_ADD_SUCCESS_EVENT = ADD_TO_CART_SUCCESS_EVENT

const listenerStates = new WeakMap()
const searchDrawerSelector = 'search-drawer#search-drawer'
const collectionCardSelector = '.shopify-section--main-collection .product-card'
const homeCardSelector = '.shopify-section--featured-collection .product-card'
const recommendationCardSelectors = Object.freeze([
  '.shopify-section--product-recommendations .product-card',
  '.lp5-compatible-products .product-card',
])
const directQuickAddContainerSelector = '.product-card__quick-buy'
const productFormSelector = 'form[is="product-form"]'
const mainProductRootSelector = '.shopify-section--main-product'
const figmaRootSelector = 'main-product-figma[data-main-product-figma]'
const addHookSelector = '[data-lp-track="custom_add_to_cart"][data-lp-track-source="vendor"]'
const faqHookSelector = '[data-lp-track="click_faq"][data-lp-track-source="vendor"]'
const faqQuestionSelector = ':scope > div:first-child > div:first-child, :scope > span:first-child'
const subscribeHookSelector = '[data-lp-track="subscribe"][data-lp-track-source="vendor"]'
const newsletterFormSelector =
  '.shopify-section-group-footer-group.shopify-section--newsletter form[action*="/contact"], form.footer__newsletter-form[action*="/contact"]'
const klaviyoFooterContainerSelector = '.shopify-section-group-footer-group, .footer'
const klaviyoFormIdPattern = /^[A-Za-z0-9]{6}$/u
const addToCartContexts = new Set([
  'collection_quick_add',
  'home_quick_add',
  'pdp_form',
  'pdp_sticky_bar',
  'pdp_recommendations',
  'campaign_product',
  'smb_product',
])
const pdpFbtSelector =
  'input[type="checkbox"][data-main-product-figma-addon][data-addon-kind="fbt"]'
const headerCategorySelectHookSelector =
  '[data-lp-track][data-lp-track-context][data-lp-track-source="vendor"]'

function resolveDataLayer(providedDataLayer) {
  if (providedDataLayer) return providedDataLayer
  if (globalThis.dataLayer == null) globalThis.dataLayer = []
  return globalThis.dataLayer
}

function resolveAddHook(form, submitter) {
  return submitter?.closest?.(addHookSelector) ?? form?.querySelector?.(addHookSelector)
}

function resolveHeaderCategorySelectHook(target) {
  const hook = target?.closest?.(headerCategorySelectHookSelector)
  if (!hook) return null
  if (hook.getAttribute?.('data-lp-track-source') !== 'vendor') return null
  if (!HEADER_CATEGORY_SELECT_EVENTS[hook.getAttribute?.('data-lp-track')]) return null
  const context = hook.getAttribute?.('data-lp-track-context')
  if (context !== 'header_desktop_nav' && context !== 'header_mobile_nav') return null
  return hook
}

function headerCategorySelectPayload(hook, interactionMethod) {
  const track = hook.getAttribute?.('data-lp-track')
  const selectEvent = HEADER_CATEGORY_SELECT_EVENTS[track]
  if (!selectEvent) return null

  const payload = {
    ...selectEvent,
    lp_track_context: hook.getAttribute?.('data-lp-track-context'),
    lp_track_source: 'vendor',
    lp_tracking_owner: 'header_category_select',
    lp_interaction_method: interactionMethod,
  }
  const itemName = String(hook.getAttribute?.('data-lp-item-name') ?? hook.textContent ?? '')
    .replace(/\s+/g, ' ')
    .trim()
  if (itemName) payload.lp_item_name = itemName

  return payload
}

function headerCategoryHoverSelection(event) {
  if (event.pointerType !== 'mouse') return null

  const hook = resolveHeaderCategorySelectHook(event.target)
  if (!hook || hook.getAttribute?.('data-lp-track-context') !== 'header_desktop_nav') return null
  if (event.relatedTarget?.nodeType != null && hook.contains?.(event.relatedTarget)) return null

  const track = hook.getAttribute?.('data-lp-track')
  if (track === 'select_top_category') {
    if (hook.getAttribute?.('data-lp-menu-trigger') == null) return null
    const menuOpenTrigger =
      hook.closest?.('lp-header')?.getAttribute?.('data-menu-open-trigger') || 'hover'
    if (menuOpenTrigger !== 'hover') return null
  }
  if (track === 'select_second_category' && hook.getAttribute?.('data-lp-category') == null) {
    return null
  }

  return { hook, payload: headerCategorySelectPayload(hook, 'hover') }
}

function hasApprovedSurface(form, context) {
  if (context === 'pdp_form' || context === 'pdp_sticky_bar') {
    return Boolean(form.closest?.(mainProductRootSelector))
  }

  if (context === 'collection_quick_add') {
    return (
      form.parentElement?.matches?.(directQuickAddContainerSelector) === true &&
      Boolean(form.closest?.(collectionCardSelector))
    )
  }

  if (context === 'home_quick_add') {
    return Boolean(form.closest?.(homeCardSelector))
  }

  if (context === 'pdp_recommendations') {
    return recommendationCardSelectors.some((selector) => Boolean(form.closest?.(selector)))
  }

  return context === 'campaign_product' || context === 'smb_product'
}

function addToCartSubmission(event) {
  const form = event.target
  if (!form?.matches?.(productFormSelector)) return null
  if (
    event.submitter?.disabled === true ||
    event.submitter?.getAttribute?.('aria-busy') === 'true'
  ) {
    return null
  }

  const hook = resolveAddHook(form, event.submitter)
  const context = hook?.getAttribute?.('data-lp-track-context')
  if (!addToCartContexts.has(context) || !hasApprovedSurface(form, context)) return null

  return {
    context,
    form,
    sectionType: hook.getAttribute?.('data-lp-section-type'),
    campaign: hook.getAttribute?.('data-lp-campaign'),
  }
}

function addToCartPayload(event, submission) {
  const item = event.detail?.items?.[0]
  if (!item) return null

  const payload = {
    ...ADD_TO_CART_SUCCESS_EVENT,
    lp_track_context: submission.context,
  }
  const itemName = item.product_title ?? item.title
  const variantId = item.variant_id ?? item.id

  if (itemName != null && String(itemName).trim() !== '') {
    payload.lp_item_name = String(itemName).trim()
  }
  if (item.product_id != null) payload.lp_product_id = String(item.product_id)
  if (variantId != null) payload.lp_variant_id = String(variantId)
  if (item.quantity != null) payload.lp_quantity = item.quantity
  if (submission.sectionType != null && String(submission.sectionType).trim() !== '') {
    payload.lp_section_type = String(submission.sectionType).trim()
  }
  if (submission.campaign != null && String(submission.campaign).trim() !== '') {
    payload.lp_campaign = String(submission.campaign).trim()
  }

  return payload
}

function pdpFbtPayload(event) {
  const input = event.target
  if (!input?.matches?.(pdpFbtSelector)) return null
  if (!input.closest?.(figmaRootSelector)) return null

  const track = input.checked ? 'select_fbt_item' : 'unselect_fbt_item'
  const payload = {
    event: `lp_${track}`,
    lp_track: track,
    lp_track_context: 'pdp_accessories',
    lp_track_source: 'vendor',
    lp_tracking_owner: 'main_product_figma_change',
  }
  const productId = input.getAttribute?.('data-main-product-figma-addon')
  const itemName = input
    .closest?.('[data-role="accessory-card"]')
    ?.querySelector?.('strong')
    ?.textContent?.trim()

  if (productId != null && String(productId).trim() !== '') {
    payload.lp_product_id = String(productId).trim()
  }
  if (itemName) payload.lp_item_name = itemName

  return payload
}

function trustedFaqPayload(event) {
  if (event.isTrusted !== true) return null
  const hook = event.target?.closest?.(faqHookSelector)
  if (!hook) return null

  const payload = { ...FAQ_CLICK_EVENT }
  const context = hook.getAttribute?.('data-lp-track-context')
  const sectionType = hook.getAttribute?.('data-lp-section-type')
  const question = hook.querySelector?.(faqQuestionSelector)
  const itemName = String(
    hook.getAttribute?.('data-lp-item-name') ?? question?.textContent ?? hook.textContent ?? '',
  )
    .replace(/\s+/g, ' ')
    .trim()

  if (context != null && String(context).trim() !== '') {
    payload.lp_track_context = String(context).trim()
  }
  if (sectionType != null && String(sectionType).trim() !== '') {
    payload.lp_section_type = String(sectionType).trim()
  }
  if (itemName) payload.lp_item_name = itemName

  return payload
}

function newsletterSubscribePayload(event) {
  const form = event.target
  if (!form?.matches?.(newsletterFormSelector)) return null
  if (
    event.submitter?.disabled === true ||
    event.submitter?.getAttribute?.('aria-busy') === 'true'
  ) {
    return null
  }

  const hook =
    event.submitter?.closest?.(subscribeHookSelector) ?? form.querySelector?.(subscribeHookSelector)
  if (hook?.disabled === true || hook?.getAttribute?.('aria-busy') === 'true') return null
  if (hook?.getAttribute?.('data-lp-track-context') !== 'footer') return null

  return { ...NEWSLETTER_SUBSCRIBE_INTENT_EVENT }
}

export function startTrackingEventAdapter(
  root = document,
  {
    dataLayer: providedDataLayer,
    enableHeaderCategorySelect = false,
    enableSearchDrawer = false,
    enableTrustedFaq = false,
    enableKlaviyoSubscribe = false,
    enableNewsletterSubscribe = false,
    enableCollectionQuickAdd = false,
    enablePdp = false,
    enableAddToCart = false,
    enablePdpFbt = false,
    klaviyoEventTarget,
  } = {},
) {
  const existing = listenerStates.get(root)
  if (existing) return existing.stop

  const dataLayer = resolveDataLayer(providedDataLayer)
  if (!root?.addEventListener || typeof dataLayer?.push !== 'function') return () => {}

  const addToCartEnabled = enableAddToCart || enableCollectionQuickAdd || enablePdp
  const pdpFbtEnabled = enablePdpFbt || enablePdp
  const pendingAddToCartSubmissions = new WeakMap()
  const hoverSelectedHeaderHooks = new WeakSet()
  const klaviyoPositions = new Map()
  const resolvedKlaviyoEventTarget =
    klaviyoEventTarget ?? (typeof window !== 'undefined' ? window : root)

  const onDialogAfterShow = (event) => {
    if (!event.target?.matches?.(searchDrawerSelector)) return
    dataLayer.push({ ...SEARCH_DRAWER_EVENT })
  }

  const onHeaderCategoryPointerOver = (event) => {
    const selection = headerCategoryHoverSelection(event)
    if (!selection?.payload) return
    hoverSelectedHeaderHooks.add(selection.hook)
    dataLayer.push(selection.payload)
  }

  const onHeaderCategoryPointerOut = (event) => {
    const hook = resolveHeaderCategorySelectHook(event.target)
    if (!hook) return
    if (event.relatedTarget?.nodeType != null && hook.contains?.(event.relatedTarget)) return
    hoverSelectedHeaderHooks.delete(hook)
  }

  const onHeaderCategoryClick = (event) => {
    const hook = resolveHeaderCategorySelectHook(event.target)
    if (!hook || hoverSelectedHeaderHooks.has(hook)) return
    const payload = headerCategorySelectPayload(hook, 'click')
    if (payload) dataLayer.push(payload)
  }

  const onTrustedFaqClick = (event) => {
    const payload = trustedFaqPayload(event)
    if (payload) dataLayer.push(payload)
  }

  const onKlaviyoForms = (event) => {
    const detail = event.detail
    const formId = detail?.formId
    if (typeof formId !== 'string' || !klaviyoFormIdPattern.test(formId)) return

    if (detail.type === 'open') {
      klaviyoPositions.set(formId, 'popup')
      return
    }

    if (detail.type === 'embedOpen') {
      const host = root.querySelector?.(`.klaviyo-form-${formId}`)
      if (host?.closest?.(klaviyoFooterContainerSelector)) {
        klaviyoPositions.set(formId, 'footer')
      } else {
        klaviyoPositions.delete(formId)
      }
      return
    }

    if (detail.type !== 'submit') return
    const context = klaviyoPositions.get(formId)
    if (!context) return

    klaviyoPositions.delete(formId)
    dataLayer.push({ ...KLAVIYO_SUBSCRIBE_SUCCESS_EVENT, lp_track_context: context })
  }

  const onNewsletterSubscribe = (event) => {
    const payload = newsletterSubscribePayload(event)
    if (payload) dataLayer.push(payload)
  }

  const onAddToCartSubmit = (event) => {
    const submission = addToCartSubmission(event)
    if (submission) pendingAddToCartSubmissions.set(submission.form, submission)
  }

  const onVariantAdd = (event) => {
    const submission = pendingAddToCartSubmissions.get(event.target)
    if (!submission) return

    pendingAddToCartSubmissions.delete(event.target)
    const payload = addToCartPayload(event, submission)
    if (payload) dataLayer.push(payload)
  }

  const onCartError = (event) => {
    pendingAddToCartSubmissions.delete(event.target)
  }

  const onPdpFbtChange = (event) => {
    const payload = pdpFbtPayload(event)
    if (payload) dataLayer.push(payload)
  }

  if (enableHeaderCategorySelect) {
    root.addEventListener('pointerover', onHeaderCategoryPointerOver)
    root.addEventListener('pointerout', onHeaderCategoryPointerOut)
    root.addEventListener('click', onHeaderCategoryClick)
  }
  if (enableSearchDrawer) root.addEventListener('dialog:after-show', onDialogAfterShow)
  if (enableTrustedFaq) root.addEventListener('click', onTrustedFaqClick)
  if (enableKlaviyoSubscribe && resolvedKlaviyoEventTarget?.addEventListener) {
    resolvedKlaviyoEventTarget.addEventListener('klaviyoForms', onKlaviyoForms)
  }
  if (enableNewsletterSubscribe) root.addEventListener('submit', onNewsletterSubscribe, true)
  if (addToCartEnabled) {
    root.addEventListener('submit', onAddToCartSubmit, true)
    root.addEventListener('variant:add', onVariantAdd)
    root.addEventListener('cart:error', onCartError)
  }
  if (pdpFbtEnabled) root.addEventListener('change', onPdpFbtChange, true)

  const state = {
    stop() {
      if (listenerStates.get(root) !== state) return
      root.removeEventListener('pointerover', onHeaderCategoryPointerOver)
      root.removeEventListener('pointerout', onHeaderCategoryPointerOut)
      root.removeEventListener('click', onHeaderCategoryClick)
      root.removeEventListener('dialog:after-show', onDialogAfterShow)
      root.removeEventListener('click', onTrustedFaqClick)
      resolvedKlaviyoEventTarget?.removeEventListener?.('klaviyoForms', onKlaviyoForms)
      root.removeEventListener('submit', onNewsletterSubscribe, true)
      root.removeEventListener('submit', onAddToCartSubmit, true)
      root.removeEventListener('variant:add', onVariantAdd)
      root.removeEventListener('cart:error', onCartError)
      root.removeEventListener('change', onPdpFbtChange, true)
      listenerStates.delete(root)
    },
  }
  listenerStates.set(root, state)
  return state.stop
}

if (typeof document !== 'undefined') {
  startTrackingEventAdapter(document, {
    enableHeaderCategorySelect: true,
    enableSearchDrawer: true,
    enableTrustedFaq: true,
    enableKlaviyoSubscribe: true,
    enableNewsletterSubscribe: true,
    enableAddToCart: true,
    enablePdpFbt: true,
  })
}
